import axios, { AxiosError } from 'axios';
import type { ClientToken } from './types';
import { EXCHANGE_URL, TOKENS_STORAGE_KEY } from './config';
import { performAutoLogin } from './auth-utils';

export class TokenManager {
  private tokenCache = new Map<string, ClientToken>();
  private refreshPromises = new Map<string, Promise<ClientToken | null>>();

  getToken(clientId: string): ClientToken | null {
    return this.tokenCache.get(clientId) || this.getTokenFromStorage(clientId);
  }

  saveToken(token: ClientToken): void {
    this.tokenCache.set(token.clientId, token);
    this.saveTokenToStorage(token);
  }

  removeToken(clientId: string): void {
    this.tokenCache.delete(clientId);
    this.removeTokenFromStorage(clientId);
  }

  clearAll(): void {
    this.tokenCache.clear();
    this.refreshPromises.clear();
    this.clearTokenStorage();
  }

  async generateToken(clientId: string): Promise<ClientToken | null> {
    if (this.refreshPromises.has(clientId)) {
      return await this.refreshPromises.get(clientId)!;
    }

    const promise = this.performTokenExchange(clientId);
    this.refreshPromises.set(clientId, promise);

    try {
      return await promise;
    } finally {
      this.refreshPromises.delete(clientId);
    }
  }

  private async performTokenExchange(
    clientId: string
  ): Promise<ClientToken | null> {
    try {

      const response = await axios.post(
        EXCHANGE_URL,
        { targetClientId: clientId },
        {
          withCredentials: true,
          headers: { 'Content-Type': 'application/json' },
        }
      );

      if (response.status === 200 && response.data.access_token) {
        const accessToken = response.data.access_token;

        const token: ClientToken = {
          clientId,
          accessToken,
        };

        this.saveToken(token);
        return token;
      }

      return null;
    } catch (error: unknown) {
      if (
        error instanceof AxiosError &&
        error.response?.status === 400 &&
        error.response?.data?.detail === 'Failed to exchange token.'
      ) {
        console.warn(
          '🔄 [NSPortal] Falha na exchange de token, executando login automático'
        );
        performAutoLogin();
      }

      console.error(
        `[NSPortal] Erro na exchange de token para ${clientId}:`,
        error
      );

      return null;
    }
  }

  private getStorageData(): Record<string, ClientToken> {
    try {
      const data = localStorage.getItem(TOKENS_STORAGE_KEY);
      return data ? JSON.parse(data) : {};
    } catch (error) {
      console.error('[NSPortal] Erro ao ler dados do localStorage:', error);
      return {};
    }
  }

  private saveStorageData(storage: Record<string, ClientToken>): void {
    try {
      localStorage.setItem(TOKENS_STORAGE_KEY, JSON.stringify(storage));
    } catch (error) {
      console.error('[NSPortal] Erro ao salvar dados no localStorage:', error);
    }
  }

  private getTokenFromStorage(clientId: string): ClientToken | null {
    const storage = this.getStorageData();
    const token = storage[clientId];

    if (token) {
      this.tokenCache.set(clientId, token);
      return token;
    }

    return null;
  }

  private saveTokenToStorage(token: ClientToken): void {
    const storage = this.getStorageData();
    storage[token.clientId] = token;
    this.saveStorageData(storage);
  }

  private removeTokenFromStorage(clientId: string): void {
    const storage = this.getStorageData();
    delete storage[clientId];
    this.saveStorageData(storage);
  }

  private clearTokenStorage(): void {
    try {
      localStorage.removeItem(TOKENS_STORAGE_KEY);
    } catch (error) {
      console.error('[NSPortal] Erro ao limpar tokens:', error);
    }
  }
}
